/*
 * Groups a list of objects into date buckets: today, yesterday, last 7 days,
 * then one bucket per calendar month.
 *
 * Buckets are returned as locale-independent descriptors (not display strings),
 * so the caller renders a localized label and the month ordering stays correct
 * regardless of the UI language.
 */
import { groupBy } from "lodash";

export type DateGroup =
  | { kind: "today" }
  | { kind: "yesterday" }
  | { kind: "last7days" }
  | { kind: "month"; year: number; month: number }; // month: 1-12

export function groupObjectByDate<T>(
  listObject: T[],
  dateField = "updatedAt",
): { group: DateGroup; items: T[] }[] {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastSevenDays = new Date(today);
  lastSevenDays.setDate(lastSevenDays.getDate() - 7);

  // Group by a stable, locale-independent key.
  // Month buckets use "YYYY-M" so they never depend on a localized month name.
  const grouped = groupBy(listObject, (item: any): string => {
    const itemDate = new Date(Reflect.get(item, dateField));
    if (itemDate.toDateString() === today.toDateString()) return "today";
    if (itemDate.toDateString() === yesterday.toDateString())
      return "yesterday";
    if (itemDate > lastSevenDays) return "last7days";
    return `${itemDate.getFullYear()}-${itemDate.getMonth() + 1}`;
  });

  const relativeGroups: { group: DateGroup; items: T[] }[] = [
    { group: { kind: "today" }, items: grouped["today"] || [] },
    { group: { kind: "yesterday" }, items: grouped["yesterday"] || [] },
    { group: { kind: "last7days" }, items: grouped["last7days"] || [] },
  ];

  const monthGroups = Object.entries(grouped)
    .filter(([key]) => key.includes("-"))
    .map(([key, items]) => {
      const [year, month] = key.split("-").map(Number);
      return { year, month, items };
    })
    .sort((a, b) => b.year - a.year || b.month - a.month)
    .map(({ year, month, items }): { group: DateGroup; items: T[] } => ({
      group: { kind: "month", year, month },
      items,
    }));

  return [...relativeGroups, ...monthGroups].filter(
    (group) => group.items.length > 0,
  );
}

/**
 * Format date time to YYYY/MM/DD HH:mm:ss
 * @param date Date or ISO string
 * @returns Formatted date time string
 */
export function formatDateTime(date: Date | string): string {
  function pad(n: number): string {
    return n.toString().padStart(2, "0");
  }

  const dt = typeof date === "string" ? new Date(date) : date;
  const year = dt.getFullYear();
  const month = pad(dt.getMonth() + 1);
  const day = pad(dt.getDate());
  const hours = pad(dt.getHours());
  const minutes = pad(dt.getMinutes());
  const seconds = pad(dt.getSeconds());

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
}
